import React, { useEffect, useState } from "react";
import axios from "axios";
import { MessageSquare, Loader2, ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Checkbox } from "./ui/checkbox";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RULES = [
  "SMS yalnızca sizi tanıyan ve davet etmek istediğiniz kişilere gönderilir.",
  "Mesaj içeriği sabittir: davetiye linkiniz ve dugunsenin.com hatırlatması. Reklam veya farklı amaçla kullanılamaz.",
  "Her numaraya yalnızca bir kez SMS gönderilir; toplam hakkınız 300 davetlidir.",
  "Davetli listenizdeki numaraları paylaşma iznine sahip olduğunuzu ve sorumluluğun size ait olduğunu kabul edersiniz.",
  "Kötüye kullanım tespit edilirse SMS gönderim hakkınız kapatılabilir.",
];

const norm = (p) => {
  let d = (p || "").replace(/\D/g, "");
  if (d.startsWith("90") && d.length === 12) d = d.slice(2);
  if (d.startsWith("0") && d.length === 11) d = d.slice(1);
  return d;
};

export const InvitationSmsBox = ({ guests, token }) => {
  const { toast } = useToast();
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const load = () => axios.get(`${API}/member/invitation/sms-status`, { headers }).then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!status) return null;
  const sentSet = new Set(status.sentTo || []);
  const pending = guests.filter((g) => norm(g.phone) && !sentSet.has(norm(g.phone)));
  const left = Math.max(0, status.limit - status.used);

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/member/invitation/sms`, { phones: pending.map((g) => g.phone), guestIds: pending.map((g) => g.id || ""), guestNames: pending.map((g) => g.name || ""), acceptedRules: true }, { headers });
      toast({ title: "SMS gönderildi", description: `${data.sent} davetliye SMS gönderildi.${data.invalid ? ` ${data.invalid} geçersiz numara atlandı.` : ""}` });
      setOpen(false); setAgree(false); load();
    } catch (e) {
      toast({ title: "Gönderilemedi", description: e?.response?.data?.detail || "Tekrar deneyin." });
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-[#C9D8F0] bg-[#F3F7FD] p-4 mb-5" data-testid="inv-sms-box">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-[#2b4c7e]">SMS ile tek tıkla hepsine gönderin</p>
          <p className="text-xs text-[#5a7096] mt-1 max-w-md">
            {status.enabled
              ? `Davetiyeniz site üzerinden SMS ile gider. ${status.used} davetliye gönderildi · kalan hakkınız ${left}.`
              : "SMS gönderimi şu an kapalı. Lütfen daha sonra tekrar deneyin."}
          </p>
        </div>
        <button onClick={() => setOpen(true)} disabled={!status.enabled || !pending.length || !left} data-testid="inv-sms-open"
          className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-[#2f6fd1] text-white text-sm font-semibold hover:opacity-90 transition-opacity shrink-0 disabled:opacity-50">
          <MessageSquare className="w-4 h-4" /> {pending.length ? `${Math.min(pending.length, left)} Davetliye SMS Gönder` : "Gönderilecek davetli yok"}
        </button>
      </div>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setAgree(false); }}>
        <DialogContent className="sm:max-w-md" data-testid="inv-sms-rules-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display text-[#2A2224]"><ShieldCheck className="w-5 h-5 text-[#9E2A3B]" /> SMS Gönderim Kuralları</DialogTitle>
            <DialogDescription>Göndermeden önce lütfen kuralları okuyup onaylayın.</DialogDescription>
          </DialogHeader>
          <ol className="space-y-2 text-sm text-[#5b4d4f] list-decimal pl-5" data-testid="inv-sms-rules-list">
            {RULES.map((r) => <li key={r}>{r}</li>)}
          </ol>
          <label className="flex items-start gap-2.5 mt-2 p-3 rounded-lg bg-[#FBF5F0] cursor-pointer text-sm text-[#2A2224]">
            <Checkbox checked={agree} onCheckedChange={(v) => setAgree(!!v)} data-testid="inv-sms-agree" className="mt-0.5" />
            SMS gönderim kurallarını okudum ve kabul ediyorum.
          </label>
          <button onClick={send} disabled={!agree || busy} data-testid="inv-sms-send"
            className="w-full h-11 rounded-xl bg-[#9E2A3B] text-white font-medium flex items-center justify-center gap-2 hover:bg-[#861f2f] transition-colors disabled:opacity-50">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Onayla ve {Math.min(pending.length, left)} Davetliye Gönder
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
};
